import { atom, useAtom } from "jotai";
import { useCallback } from "react";
import { calculatePremiums } from "service/api";
import { LoadStatus } from "./FormPageStore";

// DTO Types for API (PascalCase)
interface CalculationOrOfferRequestDto {
  SelectedProducts?: string[];
  SelectedInsurers?: string[];
  FleetNumber?: string;
  Vehicle: VehicleDto;
  Insurance: InsuranceDto;
  Leasing?: LeasingDto;
  Client?: ClientDto;
  CustomParameters?: Record<string, any[]>;
  CorrelationId?: string;
}

interface LeasingDto {
  LeasingContractNumber?: string;
  ReplacedContractNumber?: string;
}

interface VehicleDto {
  LicensePlate: string;
  Vin: string;
  Brand: string;
  Model: string;
  RegistrationCertificateNumber: string;
  PowerKw?: number;
  CubicCapacityCcm?: number;
  Seats?: number;
  Weight?: number;
  Mileage?: number;
  ManufactureDate?: Date;
  FirstRegistrationDate?: Date;
  Value?: number;
  Fuel?: string;
  VehicleCategory?: string;
  UsageType?: string;
  RegistrationType?: string;
  CountryCode?: string;
  DamagedBefore?: string;
  Repaired?: string;
  RegisteredAbroad?: string;
  TwoYearRegistration?: string;
  LeaseType?: string;
  FinancingShare?: number;
  LeaseFixedRate?: string;
  Owner?: OwnerDto;
}

interface OwnerDto {
  Code: string;
  Name?: string;
  LastName?: string;
  FirstName?: string;
  BirthDate?: Date;
  Company: boolean;
}

interface InsuranceDto {
  StartDate?: Date;
  EndDate?: Date;
  ContractStartDate?: Date;
  Payments?: number;
  CoverageTerritory?: string;
  Currency?: string;
  DriverBirthDate?: Date;
  DriverAge?: number;
  DriverLicenceDate?: Date;
  DriverExperience?: number;
}

interface ClientDto {
  Roles: string[];
  IsCompany?: boolean;
  Name?: string;
  Code?: string;
  VatPayer?: string;
  LastName?: string;
  FirstName?: string;
  BirthNumber?: string;
  BirthDate?: Date;
  Title?: string;
  TitleType?: string;
  CountryOfCitizenship?: string;
  Age?: number;
  Experience?: number;
  ElectronicCommunication?: string;
  MarketingConsent?: string;
  Address?: AddressDto;
  CorrespondenceAddress?: AddressDto;
  ContactPerson?: ContactPersonDto;
}

interface AddressDto {
  Country?: string;
  House?: string;
  Flat?: string;
  Street?: string;
  City?: string;
  Suburb?: string;
  Index?: string;
  Email?: string;
  MobilePhone?: string;
  Phone?: string;
}

interface ContactPersonDto {
  Order?: string;
  LastName?: string;
  FirstName?: string;
  Function?: string;
}

// Helper types
interface RiskParameter {
  idRisk: string;
  code: string;
  valueType: string;
  value: string;
}

interface GainerParameters {
  order: string;
  code: string;
  userId?: string;
  personalNumber?: string;
  isSigning: string;
}

// Initial state values
export const initialItemData = {
  // Contract information
  fleetNumber: "6980363900",

  // Client type selection
  clientType: "pravnicka_osoba" as "obcan" | "osvc" | "pravnicka_osoba",
  correspondenceAddressDifferent: false,
  elkom: "N" as "A" | "N",
  marketing: "N" as "V" | "A" | "N",

  // Client information
  client: {
    roles: [],
    isCompany: true,
    name: "",
    code: "45793107",
    vatPayer: true,
    lastName: "",
    firstName: "",
    birthNumber: "",
    birthDate: null as Date | null,
    title: "",
    titleType: "",
    countryOfCitizenship: "",
    age: null as number | null,
    experience: null as number | null,
    electronicCommunication: "",
    marketingConsent: "",
    // Driver-specific fields
    driverLicenceDate: null as Date | null,
    address: {
      country: "",
      house: "",
      flat: "",
      street: "",
      city: "",
      suburb: "",
      index: "",
      email: "",
      mobilePhone: "",
      phone: "",
    },
    correspondenceAddress: {
      country: "",
      house: "",
      flat: "",
      street: "",
      city: "",
      suburb: "",
      index: "",
      email: "",
      mobilePhone: "",
      phone: "",
    },
    contactPerson: {
      order: "",
      lastName: "",
      firstName: "",
      function: "",
    },
  },

  // Leasing information
  leasing: {
    leasingContractNumber: "",
    replacedContractNumber: "",
  },

  // Vehicle details
  vehicle: {
    licensePlate: "UN159195",
    vin: "TMBJR7NU3PC202404",
    brand: "",
    model: "",
    registrationCertificateNumber: "",
    powerKw: null as number | null,
    cubicCapacityCcm: null as number | null,
    seats: null as number | null,
    weight: null as number | null,
    mileage: "14000",
    manufactureDate: null as Date | null,
    firstRegistrationDate: null as Date | null,
    value: 1200000,
    fuel: "",
    vehicleCategory: "A",
    vehicleUsage: "personal",
    registrationType: "S1",
    countryCode: "CZ",
    damagedBefore: "N",
    repaired: "N",
    registeredAbroad: "N",
    twoYearRegistration: "M",
    leaseType: "F",
    financingShare: null as number | null,
    leaseFixedRate: "N",
    // Owner - keep separate as it might differ from policyholder
    owner: {
      code: "",
      name: "",
      lastName: "",
      firstName: "",
      birthDate: null as Date | null,
      company: false,
    },
  },

  // Insurance details
  insurance: {
    startDate: new Date(), //null as Date | null,
    endDate: null as Date | null,
    contractStartDate: new Date(), //null as Date | null,
    payments: 1,
    coverageTerritory: "CZE",
    currency: "CZK",
  },

  // Kooperativa-specific fields
  kooperativaFields: {
    damageSpecification: "",
    minorDamageSpecification: "",
    standingPlaces: null as number | null,
    numberOfBeds: null as number | null,
    notOperational: "",
    technicalCertificateNumber: "",
    preferredLiquidation: "",
    specialAgreement: "",
    gainers: [] as GainerParameters[],
    risks: [] as RiskParameter[],
  },

  // Selected insurers and products
  selectedProducts: ["all"] as string[],
  selectedInsurers: ["Kooperativa"] as string[],

  // Calculation results
  calculationId: null as string | null,
  calculationData: null as any,
  premiumData: {
    mtpl: {
      selected: false,
      premium: 0,
      risks: [] as any[],
    },
    casco: {
      selected: false,
      premium: 0,
      risks: [] as any[],
    },
    combined: {
      selected: false,
      premium: 0,
      risks: [] as any[],
    },
    totalPremium: 0,
    messages: {
      warnings: [] as Array<{ level: string; text: string }>,
      errors: [] as Array<{ level: string; text: string }>,
    },
  },
};

// Store atoms
const itemDataAtom = atom(initialItemData);
const loadStatusAtom = atom<LoadStatus>("idle");
const selectedTabAtom = atom<string>("contract");
const errorAtom = atom<string | null>(null);

// Helper to convert camelCase to PascalCase for DTOs
const toPascalCase = <T extends Record<string, any>>(obj: T): any => {
  if (!obj) return undefined;

  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
    result[pascalKey] = value;
  }
  return result;
};

// Custom hook for ItemPage store
export function useItemPageStore() {
  const [itemData, setItemData] = useAtom(itemDataAtom);
  const [loadStatus, setLoadStatus] = useAtom(loadStatusAtom);
  const [error, setError] = useAtom(errorAtom);
  const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom);

  // Method to update item data
  const updateItemData = useCallback(
    (data: Partial<typeof initialItemData>) => {
      setItemData((prev) => ({ ...prev, ...data }));
    },
    [setItemData]
  );

  // Method to calculate premium
  const calculatePremium = useCallback(async () => {
    try {
      setLoadStatus("loading");
      setError(null);

      // Build CustomParameters based on conditions
      const customParameters: Record<string, any[]> = {};

      // Add Kooperativa-specific custom parameters
      if (itemData.selectedInsurers.includes("Kooperativa")) {
        // Vehicle damage specifications
        if (
          itemData.vehicle.damagedBefore === "A" &&
          itemData.kooperativaFields.damageSpecification
        ) {
          customParameters["Kooperativa_DamageSpecification"] = [
            itemData.kooperativaFields.damageSpecification,
          ];
        }

        if (
          itemData.vehicle.repaired === "A" &&
          itemData.kooperativaFields.minorDamageSpecification
        ) {
          customParameters["Kooperativa_MinorDamageSpecification"] = [
            itemData.kooperativaFields.minorDamageSpecification,
          ];
        }

        // Additional vehicle parameters
        if (itemData.kooperativaFields.standingPlaces) {
          customParameters["Kooperativa_StandingPlaces"] = [
            itemData.kooperativaFields.standingPlaces,
          ];
        }

        if (itemData.kooperativaFields.numberOfBeds) {
          customParameters["Kooperativa_NumberOfBeds"] = [
            itemData.kooperativaFields.numberOfBeds,
          ];
        }

        if (itemData.kooperativaFields.notOperational) {
          customParameters["Kooperativa_NotOperational"] = [
            itemData.kooperativaFields.notOperational,
          ];
        }

        if (itemData.kooperativaFields.technicalCertificateNumber) {
          customParameters["Kooperativa_TechnicalCertificateNumber"] = [
            itemData.kooperativaFields.technicalCertificateNumber,
          ];
        }

        if (itemData.kooperativaFields.preferredLiquidation) {
          customParameters["Kooperativa_PreferredLiquidation"] = [
            itemData.kooperativaFields.preferredLiquidation,
          ];
        }

        if (itemData.kooperativaFields.specialAgreement) {
          customParameters["Kooperativa_SpecialAgreement"] = [
            itemData.kooperativaFields.specialAgreement,
          ];
        }

        // Gainers
        if (itemData.kooperativaFields.gainers.length > 0) {
          customParameters["Kooperativa_Gainers"] =
            itemData.kooperativaFields.gainers.map(toPascalCase);
        }

        // Risks
        if (
          itemData.kooperativaFields.risks &&
          itemData.kooperativaFields.risks.length > 0
        ) {
          customParameters["Kooperativa_Risks"] =
            itemData.kooperativaFields.risks.map(toPascalCase);
        } else {
          // Default Kooperativa risks if none specified
          customParameters["Kooperativa_Risks"] = [
            {
              IdRisk: "1036",
              Code: "LIMITY",
              ValueType: "int",
              Value: "150000000",
            },
          ];
        }

        // Add insurance end date to custom parameters if needed
        if (itemData.insurance.endDate) {
          customParameters["Kooperativa_EndDate"] = [
            itemData.insurance.endDate.toISOString(),
          ];
        }
      }

      // Build the calculation request
      const calculationRequest: CalculationOrOfferRequestDto = {
        SelectedProducts: itemData.selectedProducts,
        SelectedInsurers: itemData.selectedInsurers,
        FleetNumber: itemData.fleetNumber || undefined,
        Vehicle: {
          ...toPascalCase(itemData.vehicle),
          Owner: itemData.vehicle.owner
            ? toPascalCase(itemData.vehicle.owner)
            : undefined,
        },
        Insurance: {
          ...toPascalCase(itemData.insurance),
          // Map driver fields from client
          DriverBirthDate: itemData.client.birthDate || undefined,
          DriverAge: itemData.client.age || undefined,
          DriverLicenceDate: itemData.client.driverLicenceDate || undefined,
          DriverExperience: itemData.client.experience || undefined,
        },
        Leasing:
          itemData.leasing.leasingContractNumber ||
          itemData.leasing.replacedContractNumber
            ? toPascalCase(itemData.leasing)
            : undefined,
        Client: {
          ...toPascalCase(itemData.client),
          Address: itemData.client.address
            ? toPascalCase(itemData.client.address)
            : undefined,
          CorrespondenceAddress: itemData.client.correspondenceAddress
            ? toPascalCase(itemData.client.correspondenceAddress)
            : undefined,
          ContactPerson: itemData.client.contactPerson
            ? toPascalCase(itemData.client.contactPerson)
            : undefined,
        },
        CustomParameters:
          Object.keys(customParameters).length > 0
            ? customParameters
            : undefined,
      };

      // Call the API
      const apiResponse = await calculatePremiums(calculationRequest);

      // Process the response
      const processedPremiumData = processCalculationResponse(apiResponse);

      // Extract calculation ID from the response
      const calculationId =
        apiResponse.data.CalculationId ||
        apiResponse.data.calculationId ||
        null;

      // Update state with results
      setItemData((prev) => ({
        ...prev,
        calculationId: calculationId,
        calculationData: apiResponse,
        premiumData: processedPremiumData,
      }));

      setLoadStatus("succeeded");

      return {
        premiumData: processedPremiumData,
        calculationId: calculationId,
        rawResponse: apiResponse,
      };
    } catch (err) {
      console.error("Calculation error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to calculate premium";
      setError(errorMessage);
      setLoadStatus("failed");
      throw err;
    }
  }, [itemData, setItemData, setLoadStatus, setError]);

  const reset = useCallback(() => {
    setItemData(initialItemData);
    setLoadStatus("idle");
    setError(null);
    setSelectedTab("contract");
  }, [setItemData, setLoadStatus, setError, setSelectedTab]);

  return [
    {
      itemData,
      loadStatus,
      error,
      selectedTab,
      isLoading: loadStatus === "loading",
    },
    {
      updateItemData,
      calculatePremium,
      setSelectedTab,
      reset,
    },
  ] as const;
}

// Helper function to process the API response
const processCalculationResponse = (apiResponse: any) => {
  const premiumData = {
    mtpl: {
      selected: false,
      premium: 0,
      risks: [] as any[],
    },
    casco: {
      selected: false,
      premium: 0,
      risks: [] as any[],
    },
    combined: {
      selected: false,
      premium: 0,
      risks: [] as any[],
    },
    totalPremium: 0,
    messages: {
      warnings: [] as Array<{ level: string; text: string }>,
      errors: [] as Array<{ level: string; text: string }>,
    },
  };

  if (apiResponse?.Calculation?.Response) {
    const response = apiResponse.Calculation.Response;

    // Process each type of response
    const processResponseType = (
      type: "Mtpl" | "Casco" | "Combined",
      target: "mtpl" | "casco" | "combined"
    ) => {
      if (response[type] && response[type].length > 0) {
        response[type].forEach((typeResponse: any) => {
          if (typeResponse.Response) {
            premiumData[target].premium = parseFloat(
              typeResponse.Response.Premium || 0
            );
            premiumData[target].risks = typeResponse.Response.Risks || [];
            premiumData[target].selected = true;

            // Extract messages
            if (typeResponse.Response.Messages) {
              typeResponse.Response.Messages.forEach((msg: any) => {
                const message = { level: msg.Level, text: msg.Text };
                if (msg.Level === "1") {
                  premiumData.messages.warnings.push(message);
                } else if (msg.Level === "2") {
                  premiumData.messages.errors.push(message);
                }
              });
            }
          }
        });
      }
    };

    processResponseType("Mtpl", "mtpl");
    processResponseType("Casco", "casco");
    processResponseType("Combined", "combined");

    // Calculate total premium
    premiumData.totalPremium =
      (premiumData.mtpl.selected ? premiumData.mtpl.premium : 0) +
      (premiumData.casco.selected ? premiumData.casco.premium : 0) +
      (premiumData.combined.selected ? premiumData.combined.premium : 0);
  }

  return premiumData;
};

// Hook to get calculation messages for display
export function useCalculationMessages() {
  const [{ itemData }] = useItemPageStore();
  return {
    warnings: itemData.premiumData.messages.warnings,
    errors: itemData.premiumData.messages.errors,
    hasWarnings: itemData.premiumData.messages.warnings.length > 0,
    hasErrors: itemData.premiumData.messages.errors.length > 0,
    hasMessages:
      itemData.premiumData.messages.warnings.length > 0 ||
      itemData.premiumData.messages.errors.length > 0,
  };
}
